import { inject, Injectable } from '@angular/core';
import { UpdateGeneralSettingsDto } from '../general/dtos/update-general-settings.dto';
import { GeneralSettingsModel } from '../general/model/general-settings.model';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap, shareReplay } from 'rxjs';
import { environment } from '@env/environment';
import { ErrorHandlingService } from '@core/services/error-handling/error-handling.service';
import { UpdateAppointmentSettingsDto } from '../appointment/dtos/update-appointment-settings.dto';
import { AppointmentSettingsModel } from '../appointment/model/appointment-settings.model';
import { BillingSettingsModel } from '@features/system/settings/billing/model/billing-settings.model';
import { UpdateBillingSettingsDto } from '@features/system/settings/billing/dtos/update-billing-settings.dto';
import { CommunicationSettingsModel } from '../communication/models/communication-settings.model';
import { UpdateCommunicationSettingsDto } from '../communication/dto/update-communication-settings.dto';
import { UpdateSecuritySettingsDto } from '../security/dto/update-securtiy-settings.dto';
import { SecuritySettingsModel } from '../security/model/security-settings.model';
import { User } from '@features/org-and-users/users/models/user.model';
import { ClinicalSettingsModel } from '@features/system/settings/clinical/models/clinical-settings.model';
import { UpdateClinicalSettingsDto } from '@features/system/settings/clinical/dtos/update-clinical-settings.dto';
import { CryptoService } from '@core/services/encryption/crypto.service';
import { QueryParamsMap } from '@features/system/settings/models/settings.model';

export interface PatientEncryptionPolicy {
  encryptionModel: 'ZERO_KNOWLEDGE' | 'MANAGED';
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private apiUrl = environment.apiBaseUrl;
  private http = inject(HttpClient);
  private errorHandlingService = inject(ErrorHandlingService);
  private cryptoService = inject(CryptoService);

  private securitySettingsCache: SecuritySettingsModel | null = null;
  private patientEncryptionPolicyCache$: Observable<PatientEncryptionPolicy> | null = null;

  constructor() { }

  // General Services

  getGeneralSettings(): Observable<GeneralSettingsModel> {
    return this.http
      .get<GeneralSettingsModel>(`${this.apiUrl}settings/general`, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  updateGeneralSettings(
    settings: UpdateGeneralSettingsDto,
  ): Observable<GeneralSettingsModel> {
    return this.http
      .put<GeneralSettingsModel>(`${this.apiUrl}settings/general`, settings, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  // Appointment Services

  getAppointmentSettings(
    providerId?: string,
  ): Observable<AppointmentSettingsModel> {
    const params: QueryParamsMap = {};

    if (providerId) {
      params['providerId'] = providerId;
    }

    return this.http
      .get<AppointmentSettingsModel>(`${this.apiUrl}settings/appointment`, {
        withCredentials: true,
        params,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  getEffectiveAppointmentSettings(
    providerId: string,
  ): Observable<AppointmentSettingsModel> {
    const params: QueryParamsMap = {};

    return this.http
      .get<AppointmentSettingsModel>(
        `${this.apiUrl}settings/appointment/effective-appointment/${providerId}`,
        {
          withCredentials: true,
        },
      )
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  updateAppointmentSettings(
    settings: UpdateAppointmentSettingsDto,
  ): Observable<AppointmentSettingsModel> {
    return this.http
      .put<AppointmentSettingsModel>(
        `${this.apiUrl}settings/appointment`,
        settings,
        {
          withCredentials: true,
        },
      )
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  public getUsersByRoleName(role: { roleName: string }): Observable<User[]> {
    return this.http
      .post<User[]>(`${this.apiUrl}role/users-by-role`, role, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  // Billing Services
  getBillingSettings(): Observable<BillingSettingsModel> {
    return this.http
      .get<BillingSettingsModel>(`${this.apiUrl}settings/billing`, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  updateBillingSettings(
    settings: UpdateBillingSettingsDto,
  ): Observable<BillingSettingsModel> {
    return this.http
      .put<BillingSettingsModel>(`${this.apiUrl}settings/billing`, settings, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  // Communication Services
  getCommunicationSettings(): Observable<CommunicationSettingsModel> {
    return this.http
      .get<CommunicationSettingsModel>(`${this.apiUrl}settings/communication`, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  updateCommunicationSettings(
    settings: UpdateCommunicationSettingsDto,
  ): Observable<CommunicationSettingsModel> {
    return this.http
      .put<CommunicationSettingsModel>(
        `${this.apiUrl}settings/communication`,
        settings,
        {
          withCredentials: true,
        },
      )
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  // Securtity Services
  getSecurtitySettings(): Observable<SecuritySettingsModel> {
    if (this.securitySettingsCache) {
      return of(this.securitySettingsCache);
    }
    return this.http
      .get<SecuritySettingsModel>(`${this.apiUrl}settings/security`, {
        withCredentials: true,
      })
      .pipe(
        tap((settings) => {
          this.securitySettingsCache = settings;
          this.cryptoService.setEncryptedSearchFields(
            settings.encryptedSearchFields || [],
          );
        }),
        catchError((err) => this.errorHandlingService.handleError(err)),
      );
  }

  updatSecurtitySettings(
    settings: UpdateSecuritySettingsDto,
  ): Observable<SecuritySettingsModel> {
    return this.http
      .put<SecuritySettingsModel>(`${this.apiUrl}settings/security`, settings, {
        withCredentials: true,
      })
      .pipe(
        tap((updatedSettings) => {
          this.securitySettingsCache = updatedSettings;
          this.cryptoService.setEncryptedSearchFields(
            updatedSettings.encryptedSearchFields || [],
          );
        }),
        catchError((err) => this.errorHandlingService.handleError(err)),
      );
  }

  getPatientDataLockout(): Observable<{ patientDataLockoutInMinutes: number }> {
    return this.http
      .get<{ patientDataLockoutInMinutes: number }>(
        `${this.apiUrl}settings/security/patient-data-lockout`,
        {
          withCredentials: true,
        },
      )
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  getPatientEncryptionPolicy(): Observable<PatientEncryptionPolicy> {
    if (!this.patientEncryptionPolicyCache$) {
      this.patientEncryptionPolicyCache$ = this.http
        .get<PatientEncryptionPolicy>(`${this.apiUrl}patient/encryption-policy`, {
          withCredentials: true,
        })
        .pipe(
          shareReplay(1),
          catchError((err) => {
            this.patientEncryptionPolicyCache$ = null;
            return this.errorHandlingService.handleError(err);
          })
        );
    }
    return this.patientEncryptionPolicyCache$;
  }

  // Clinical Services
  getClinicalSettings(): Observable<ClinicalSettingsModel> {
    return this.http
      .get<ClinicalSettingsModel>(`${this.apiUrl}settings/clinical`, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  updateClinicalSettings(
    settings: UpdateClinicalSettingsDto,
  ): Observable<ClinicalSettingsModel> {
    return this.http
      .put<ClinicalSettingsModel>(`${this.apiUrl}settings/clinical`, settings, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }


  // Tax Services
  getTaxSettings(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}settings/tax`, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }

  updateTaxSettings(settings: any): Observable<any> {
    return this.http
      .patch<any>(`${this.apiUrl}settings/tax`, settings, {
        withCredentials: true,
      })
      .pipe(catchError((err) => this.errorHandlingService.handleError(err)));
  }
}
